import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { changePassword } from "../api/auth";
import { WwwPortalNavBar } from "./WwwPortalNavBar";
import "./password-reset.css";

export const ChangePassword = (): JSX.Element => {
    const { t } = useTranslation();
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);

        if (newPassword !== confirmPassword) {
            setError(t("app.changePassword.error.mismatch"));
            return;
        }

        if (newPassword.length < 8) {
            setError(t("app.changePassword.error.minLength"));
            return;
        }

        setIsLoading(true);
        try {
            await changePassword(currentPassword, newPassword);
            setSuccess(true);
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
        } catch (err: unknown) {
            const ax = err as { response?: { data?: { message?: string; title?: string } }; message?: string };
            const msg =
                ax.response?.data?.message ||
                ax.response?.data?.title ||
                ax.message ||
                t("app.changePassword.error.generic");
            setError(msg);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="password-reset-container">
            <div className="password-reset-portal-nav">
                <WwwPortalNavBar surface="www_other" />
            </div>
            <div className="password-reset-card">
                <div className="password-reset-header">
                    <h1>{t("app.changePassword.title")}</h1>
                    <p className="password-reset-subtitle">{t("app.changePassword.subtitle")}</p>
                </div>

                <form onSubmit={handleSubmit} className="password-reset-form">
                    {error && (
                        <div className="password-reset-error" role="alert">
                            {error}
                        </div>
                    )}

                    {success && (
                        <div className="password-reset-success" role="alert">
                            {t("app.changePassword.success")}
                        </div>
                    )}

                    <div className="password-reset-field">
                        <label htmlFor="currentPassword">{t("app.changePassword.currentLabel")}</label>
                        <input
                            id="currentPassword"
                            type="password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            autoComplete="current-password"
                            required
                            disabled={isLoading}
                        />
                    </div>

                    <div className="password-reset-field">
                        <label htmlFor="newPasswordChange">{t("app.changePassword.newLabel")}</label>
                        <input
                            id="newPasswordChange"
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            autoComplete="new-password"
                            required
                            disabled={isLoading}
                            minLength={8}
                        />
                        <p className="password-reset-hint">{t("app.changePassword.hint")}</p>
                    </div>

                    <div className="password-reset-field">
                        <label htmlFor="confirmPasswordChange">{t("app.changePassword.confirmLabel")}</label>
                        <input
                            id="confirmPasswordChange"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            autoComplete="new-password"
                            required
                            disabled={isLoading}
                            minLength={8}
                        />
                    </div>

                    <button type="submit" className="password-reset-button" disabled={isLoading}>
                        {isLoading ? t("app.changePassword.saving") : t("app.changePassword.submit")}
                    </button>
                </form>

                <div className="password-reset-link">
                    <Link to="/">{t("app.changePassword.backToMap")}</Link>
                </div>
            </div>
        </div>
    );
};
